'use client'

import { useState, memo } from 'react'
import {
  UserIcon,
  WaveformIcon,
  TagIcon,
  CalendarBlankIcon,
} from '@phosphor-icons/react'
import { DeleteButton } from '@/ui/delete-button'
import { EditButton } from '@/ui/edit-button'
import { MarkdownContent } from '@/ui/markdown-content'
import { formatRelativeTime } from '@/lib/date'
import {
  ExpandableRow,
  RowHeader,
  RowActions,
  PeopleList,
  TopicBadges,
  TypeBadge,
  MetaTagsRow,
  MetaChip,
} from '@/feed/row-chrome'
import type { RowProps } from '../../types'
import type { ReleaseEntry } from '../meta'
import type { MediaDraft } from '../write-spec'
import { SourceLink } from '@/feed/row-chrome'
import { ReleaseEditForm } from './EditForms'

interface ReleaseMetadata {
  coverUrl?: string
  year?: number
}

type ReleaseRowProps = RowProps<ReleaseEntry, MediaDraft>

export const ReleaseRow = memo(
  function ReleaseRow({
    entry,
    isExpanded,
    onToggle,
    onDelete,
    onSave,
    isDeleting,
    isSaving,
  }: ReleaseRowProps) {
    const metadata = entry.metadata as ReleaseMetadata
    const people = entry.people || []
    const topics = entry.topics || []
    const [isEditing, setIsEditing] = useState(false)
    const [editTitle, setEditTitle] = useState(entry.title)
    const [editYear, setEditYear] = useState(metadata.year?.toString() || '')
    const [editTopics, setEditTopics] = useState(
      topics?.map((t) => t.name).join(', ') || '',
    )
    const [editNotes, setEditNotes] = useState(entry.content || '')

    const handleStartEdit = (e: React.MouseEvent) => {
      e.stopPropagation()
      setIsEditing(true)
      setEditTitle(entry.title)
      setEditYear(metadata.year?.toString() || '')
      setEditTopics(topics?.map((t) => t.name).join(', ') || '')
      setEditNotes(entry.content || '')
    }

    const handleCancelEdit = () => {
      setIsEditing(false)
    }

    const handleSave = async () => {
      const topicsArray = editTopics
        .split(',')
        .map((g: string) => g.trim())
        .filter(Boolean)
      await onSave({
        title: editTitle,
        year: editYear ? parseInt(editYear, 10) : null,
        topics: topicsArray.length > 0 ? topicsArray : undefined,
        notes: editNotes || null,
      })
      setIsEditing(false)
    }

    const header = (
      <RowHeader
        onToggle={onToggle}
        isExpanded={isExpanded}
        icon={
          <WaveformIcon
            className="text-entry-music h-5 w-5 sm:h-4 sm:w-4"
            weight="duotone"
          />
        }
        title={entry.title}
        titleSuffix={
          topics.length > 0 && (
            <span className="hidden sm:inline">
              <TopicBadges topics={topics} maxDisplay={2} />
            </span>
          )
        }
        metadata={
          <>
            {metadata.year && (
              <span className="text-muted-foreground hidden text-xs sm:inline">
                {metadata.year}
              </span>
            )}
            <span className="text-muted-foreground/50 hidden text-xs sm:inline">
              {formatRelativeTime(entry.createdAt)}
            </span>
          </>
        }
      />
    )

    return (
      <ExpandableRow
        isExpanded={isExpanded}
        entryType="release"
        header={header}
      >
        <div className="px-2 pt-1 pb-2 sm:pr-12 sm:pb-4 sm:pl-12">
          {isEditing ? (
            <ReleaseEditForm
              editTitle={editTitle}
              setEditTitle={setEditTitle}
              editYear={editYear}
              setEditYear={setEditYear}
              editTopics={editTopics}
              setEditTopics={setEditTopics}
              editNotes={editNotes}
              setEditNotes={setEditNotes}
              isSaving={isSaving}
              onCancel={handleCancelEdit}
              onSave={handleSave}
            />
          ) : (
            <>
              {entry.content && (
                <MarkdownContent className="text-muted-foreground mb-2 text-sm italic">
                  {entry.content}
                </MarkdownContent>
              )}

              {/* Read-only meta chips */}
              <MetaTagsRow className="mb-2">
                <TypeBadge type="Release" entryType="release" />
                {metadata.year && (
                  <MetaChip icon={<CalendarBlankIcon className="h-3 w-3" />}>
                    {metadata.year}
                  </MetaChip>
                )}
                {topics.length > 0 &&
                  topics.map((topic) => (
                    <MetaChip
                      key={topic.id}
                      icon={<TagIcon className="h-3 w-3" />}
                    >
                      {topic.name}
                    </MetaChip>
                  ))}
              </MetaTagsRow>

              {people.length > 0 && (
                <div className="mb-2">
                  <PeopleList
                    people={people}
                    icon={<UserIcon className="h-3 w-3" />}
                  />
                </div>
              )}

              <div className="flex items-center gap-3">
                <SourceLink entry={entry} />
                <div className="flex-1" />
                <RowActions className="justify-end">
                  <EditButton
                    onClick={handleStartEdit}
                    variant="icon"
                    className="opacity-100 sm:opacity-0 sm:transition-opacity sm:group-hover:opacity-100"
                  />
                  <DeleteButton
                    onClick={onDelete}
                    isDeleting={isDeleting}
                    variant="icon"
                    className="opacity-100 sm:opacity-0 sm:transition-opacity sm:group-hover:opacity-100"
                  />
                </RowActions>
              </div>
            </>
          )}
        </div>
      </ExpandableRow>
    )
  },
  (prevProps, nextProps) =>
    prevProps.entry.id === nextProps.entry.id &&
    prevProps.entry.title === nextProps.entry.title &&
    prevProps.isExpanded === nextProps.isExpanded &&
    prevProps.isDeleting === nextProps.isDeleting &&
    prevProps.isSaving === nextProps.isSaving &&
    prevProps.entry.url === nextProps.entry.url,
)
